import { supabase } from '@/integrations/supabase/client';
import { captureWebRuntimeSentryEvent } from '@/lib/sentry';

type RuntimeSeverity = 'error' | 'warning';

type RuntimeEventInput = {
  userId: string;
  severity?: RuntimeSeverity;
  source: string;
  message: string;
  path?: string;
  metadata?: Record<string, unknown>;
};

const RECENT_EVENT_WINDOW_MS = 60_000;
const recentEventMap = new Map<string, number>();

function trimText(value: string, max: number) {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function getFingerprint(input: RuntimeEventInput) {
  return [
    input.userId,
    input.severity || 'error',
    input.source,
    input.path || '',
    input.message,
  ].join('::');
}

function pruneRecentEvents() {
  const now = Date.now();
  for (const [key, seenAt] of recentEventMap.entries()) {
    if (now - seenAt > RECENT_EVENT_WINDOW_MS) {
      recentEventMap.delete(key);
    }
  }
}

export async function reportRuntimeEvent(input: RuntimeEventInput) {
  if (!input.userId || !input.message.trim()) return;

  pruneRecentEvents();
  const fingerprint = getFingerprint(input);
  if (recentEventMap.has(fingerprint)) return;
  recentEventMap.set(fingerprint, Date.now());

  captureWebRuntimeSentryEvent({
    severity: input.severity,
    source: input.source,
    message: input.message,
    path: input.path,
    metadata: input.metadata,
  });

  try {
    await supabase.from('app_runtime_events' as any).insert({
      user_id: input.userId,
      severity: input.severity || 'error',
      source: trimText(input.source, 120),
      path: input.path || null,
      message: trimText(input.message, 400),
      metadata: input.metadata || {},
    });
  } catch (error) {
    console.error('Runtime event logging failed', error);
  }
}
